import { Trans, useLingui } from '@lingui/react/macro'
import { type PropertyWithRelations } from '@markku/types'
import { Card, Group, NumberInput, Stack, Text } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { useState } from 'react'
import { propertyHooks } from '$src/lib/trpcHooks'

interface PropertyAddressCardProps {
  property: PropertyWithRelations
}

export function PropertyAddressCard({ property }: PropertyAddressCardProps) {
  const { t } = useLingui()
  const { address } = property
  const existingData =
    property.data && typeof property.data === 'object' ? property.data : {}
  const existingFloorArea =
    'floorArea' in existingData && existingData.floorArea
      ? Number(existingData.floorArea)
      : undefined

  const [floorArea, setFloorArea] = useState<number | string>(
    existingFloorArea ?? '',
  )
  const { mutateAsync: updateProperty, isPending: isUpdating } =
    propertyHooks.useUpdate()

  if (!address) return null

  const handleFloorAreaUpdate = async () => {
    if (!property?.id || !floorArea) return

    await updateProperty({
      id: property.id,
      data: {
        data: {
          ...existingData,
          floorArea: Number(floorArea),
        },
      },
    })
    // No need to invalidate - React Query will auto-refetch when floorArea changes in parent
  }

  const hasFloorArea = existingFloorArea !== undefined

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            <Trans>Address</Trans>
          </Text>
          <Text fw={500}>
            {address.street}, {address.postalCode} {address.city}
          </Text>
        </Stack>

        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              <Trans>Total floor area</Trans>
            </Text>
            {!hasFloorArea && (
              <IconInfoCircle
                size={14}
                style={{ color: 'var(--mantine-color-blue-6)' }}
              />
            )}
          </Group>
          {hasFloorArea ? (
            <Text fw={500}>{existingFloorArea} m²</Text>
          ) : (
            <>
              <NumberInput
                placeholder={t`E.g. 150`}
                suffix=" m²"
                value={floorArea}
                onChange={setFloorArea}
                onBlur={handleFloorAreaUpdate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFloorAreaUpdate()
                  }
                }}
                min={0}
                max={500}
                size="sm"
                disabled={isUpdating}
              />
              <Text size="xs" c="dimmed">
                <Trans>
                  Enter the total floor area to see all suitable packages
                </Trans>
              </Text>
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
